import { invokeEdgeFunction } from "@/services/edgeApi"
import type { PanchayatDataMap } from "./panchayat.types"
import type { V2Hierarchy, V2Names, V2Ward } from "./xlsx-loader"

export const SAMPLE_PANCHAYAT_MAP: PanchayatDataMap = {}
const PERF_DEBUG_SESSION_ID = "slow-page-load"

/* --------------------------------------------------------------------------
 * BFF endpoints — Node backend (http://localhost:8080 by default).
 * Fallback order:
 *   1. VITE_BFF_URL env if set
 *   2. Same host /api/* proxy if present (not configured)
 *   3. Default http://localhost:8080
 * This keeps Supabase service credentials server-side only; the browser
 * never ships a service-role key, and calls are scoped per selection stage.
 * -------------------------------------------------------------------------- */
const BFF_URL = (() => {
  const env = String(((import.meta as any)?.env?.VITE_BFF_URL) || "").replace(/\/$/, "")
  if (env) return env
  // In Vite dev server we ship a /bff -> http://localhost:8080 proxy (rewrites path, injects bearer token).
  // Use same-origin proxy when available (avoids CORS/sandbox port issues between renderer & backend).
  if (typeof location !== "undefined" && Number(location.port || 0) === 5173) {
    return `${location.protocol}//${location.host}/bff`
  }
  // Fallback direct BFF URL (production or manual run of built bundle)
  const fallback = (typeof location !== "undefined"
    ? `${location.protocol}//${location.hostname}:8080`
    : "") || "http://localhost:8080"
  return fallback.replace(/\/$/, "")
})()

const BFF_API_KEY = String((import.meta as any)?.env?.VITE_PANCHAYAT_API_KEY || "dev-api-key")

async function bffPost(path: string, body: Record<string, unknown>): Promise<any> {
  const url = `${BFF_URL}${path}`
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${BFF_API_KEY}`,
    },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const err = new Error(`BFF ${path} failed (${res.status})`) as any
    err.status = res.status
    throw err
  }
  return res.json()
}

function reportPerfDebug(hypothesisId: string, location: string, msg: string, data: Record<string, unknown>) {
  if (!(import.meta as any)?.env?.DEV) return
  if (typeof window === "undefined" || (window as any).__TRAE_PERF_DEBUG__ !== true) return
  if (typeof fetch !== "function") return
  const perfDebugUrl = String((window as any).__TRAE_LOCAL_DEBUG_URL__ || "").trim()
  if (!perfDebugUrl) return
  fetch(perfDebugUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      sessionId: PERF_DEBUG_SESSION_ID,
      runId: "phase-14",
      hypothesisId,
      location,
      msg,
      data,
      ts: Date.now(),
    }),
  }).catch(() => { })
}

export type PanchayatDistrictRecord = {
  id: string
  names: V2Names
}

export type PanchayatBlockRecord = {
  id: string
  names: V2Names
  districtId: string
}

export type PanchayatGramPanchayatRecord = {
  id: string
  names: V2Names
  districtId: string
  blockId: string
}

export type PanchayatInstitutionRecord = {
  id: string
  code: string
  type: "current" | "previous"
  category: "gp"
  names: V2Names
  districtId: string
  blockId: string
  districtNames: V2Names
  blockNames: V2Names
  sarpanch: V2Names
  wards: V2Ward[]
  latitude?: number | null
  longitude?: number | null
  members?: unknown
  rawData?: Record<string, any>
}

export interface PanchayatDataProvider {
  getDistricts(): Promise<PanchayatDistrictRecord[]>
  getBlocks(districtId: string): Promise<PanchayatBlockRecord[]>
  getGramPanchayats(blockId: string, districtId?: string): Promise<PanchayatGramPanchayatRecord[]>
  getInstitution(
    gpId: string,
    districtId?: string,
    blockId?: string,
    recordType?: "current" | "previous",
    lang?: "en" | "hi" | "pa",
  ): Promise<PanchayatInstitutionRecord | null>
}

type InstitutionNodeType = "district" | "block" | "gp"

type InstitutionNode = {
  code?: string
  id?: string
  type?: InstitutionNodeType
  parent_code?: string | null
  district_id?: string
  name?: Partial<V2Names>
  names?: Partial<V2Names>
  name_english?: string
  name_hindi?: string
}

type PanchayatChildrenPayload = {
  type?: InstitutionNodeType | null
  parent_code?: string | null
  children?: InstitutionNode[]
}

type DistrictBootstrapPayload = {
  districts?: Array<{
    id?: string
    names?: Partial<V2Names>
  }>
}

type PanchayatDataPayload = {
  code?: string
  type?: "current" | "previous"
  category?: "district" | "block" | "gp"
  data?: Record<string, any>
  panchayat?: {
    id?: string
    code?: string
    type?: "current" | "previous"
    category?: "gp"
    district?: string
    block?: string
    gram_panchayat?: string
    latitude?: number | null
    longitude?: number | null
    members?: unknown
    data?: Record<string, any>
  }
}

type CacheEntry<T> = {
  value: T
  expiresAt: number
}

const DISTRICT_TTL_MS = 24 * 60 * 60_000
const BLOCK_TTL_MS = 6 * 60 * 60_000
const GP_TTL_MS = 6 * 60 * 60_000
const INSTITUTION_TTL_MS = 30 * 60_000

const DATASET_VERSION_POLL_MS = 5 * 60_000

let districtListCache: CacheEntry<PanchayatDistrictRecord[]> | null = null
let districtListInFlight: Promise<PanchayatDistrictRecord[]> | null = null
const blockListCache = new Map<string, CacheEntry<PanchayatBlockRecord[]>>()
const blockListInFlightCache = new Map<string, Promise<PanchayatBlockRecord[]>>()
const gpListCache = new Map<string, CacheEntry<PanchayatGramPanchayatRecord[]>>()
const gpListInFlightCache = new Map<string, Promise<PanchayatGramPanchayatRecord[]>>()
const institutionCache = new Map<string, CacheEntry<PanchayatInstitutionRecord | null>>()
const institutionInFlightCache = new Map<string, Promise<PanchayatInstitutionRecord | null>>()
let activePanchayatDataProvider: PanchayatDataProvider | null = null
let hierarchyInFlight: Promise<V2Hierarchy> | null = null
const loadedDistrictByBlockKey = new Map<string, string>()
const loadedDistrictByGpKey = new Map<string, string>()
const loadedBlockByGpKey = new Map<string, string>()
const EMPTY_NAMES: V2Names = { en: "", hi: "", pa: "" }

type DatasetMetaPayload = {
  version?: unknown
}

let datasetVersion: string | null = null
let datasetVersionCheckedAt = 0
let datasetVersionInFlight: Promise<string | null> | null = null

function normalizeString(value: unknown) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
}

function readFreshCacheEntry<T>(entry: CacheEntry<T> | null | undefined): CacheEntry<T> | null {
  if (!entry) return null
  if (Date.now() >= entry.expiresAt) return null
  return entry
}

async function loadDatasetVersion(): Promise<string | null> {
  const now = Date.now()
  if (datasetVersion && now - datasetVersionCheckedAt < DATASET_VERSION_POLL_MS) return datasetVersion
  if (!datasetVersion && datasetVersionCheckedAt && now - datasetVersionCheckedAt < DATASET_VERSION_POLL_MS) return null
  if (datasetVersionInFlight) return datasetVersionInFlight

  const request = (async () => {
    try {
      const res = await fetch("/data/npl_sync_meta.json", { cache: "no-cache" })
      if (!res.ok) return null
      const parsed = (await res.json()) as DatasetMetaPayload
      const version = typeof parsed?.version === "string" ? parsed.version.trim() : ""
      return version || null
    } catch {
      return null
    } finally {
      datasetVersionCheckedAt = Date.now()
    }
  })()

  datasetVersionInFlight = request
  try {
    return await request
  } finally {
    if (datasetVersionInFlight === request) datasetVersionInFlight = null
  }
}

async function ensureDatasetVersion(): Promise<void> {
  const next = await loadDatasetVersion()
  if (!next) return
  if (!datasetVersion) {
    datasetVersion = next
    return
  }
  if (datasetVersion === next) return
  datasetVersion = next
  invalidatePanchayatDatasetCache()
}

function normalizeNames(value: any): V2Names {
  const en = String(value?.en || value?.name || "").trim()
  const hi = String(value?.hi || en).trim()
  const pa = String(value?.pa || en).trim()
  return {
    en,
    hi,
    pa,
  }
}

function normalizeDistrictNodeNames(value: InstitutionNode): V2Names {
  const en = String(value?.name_english || value?.name?.en || value?.code || value?.id || "").trim()
  const hi = String(value?.name_hindi || value?.name?.hi || en).trim()
  const pa = String(value?.name?.pa || en).trim()
  return { en, hi, pa }
}

function normalizeBlockNodeNames(value: InstitutionNode): V2Names {
  const en = String(value?.name_english || value?.name?.en || value?.names?.en || value?.code || value?.id || "").trim()
  const hi = String(value?.name_hindi || value?.name?.hi || value?.names?.hi || en).trim()
  const pa = String(value?.name?.pa || value?.names?.pa || en).trim()
  return { en, hi, pa }
}

function normalizeGpNodeNames(value: InstitutionNode): V2Names {
  const en = String(value?.name_english || value?.name?.en || value?.names?.en || value?.code || value?.id || "").trim()
  const hi = String(value?.name_hindi || value?.name?.hi || value?.names?.hi || en).trim()
  const pa = String(value?.name?.pa || value?.names?.pa || en).trim()
  return { en, hi, pa }
}

function normalizeWardNames(raw: Record<string, any>, wardNumber: number): V2Names {
  return {
    en: String(raw?.en?.[`ward${wardNumber}`] || "").trim(),
    hi: String(raw?.hi?.[`ward${wardNumber}`] || "").trim(),
    pa: String(raw?.pa?.[`ward${wardNumber}`] || "").trim(),
  }
}

function normalizeDistrictKey(value: string) {
  return normalizeString(value)
}

function normalizeBlockKey(blockId: string, districtId?: string) {
  return `${normalizeString(districtId || "")}::${normalizeString(blockId)}`
}

function normalizeGpKey(gpId: string, districtId?: string, blockId?: string) {
  return `${normalizeString(districtId || "")}::${normalizeString(blockId || "")}::${normalizeString(gpId)}`
}

function matchesRecordNamesStrictId(
  value: { id: string; names: V2Names },
  target: string,
): boolean {
  const normalizedTarget = normalizeString(target)
  if (!normalizedTarget) return false
  return normalizeString(value.id) === normalizedTarget
}

function matchesRecordNames(
  value: { id: string; names: V2Names },
  target: string,
): boolean {
  const normalizedTarget = normalizeString(target)
  if (!normalizedTarget) return false
  if (normalizeString(value.id) === normalizedTarget) return true
  return Object.values(value.names || EMPTY_NAMES).some((name) => normalizeString(name) === normalizedTarget)
}

function findDistrictByIdOrLegacyName(
  districts: PanchayatDistrictRecord[],
  target: string,
): PanchayatDistrictRecord | null {
  const strict = districts.find((d) => matchesRecordNamesStrictId(d, target)) || null
  if (strict) return strict
  const candidates = districts.filter((d) => matchesRecordNames(d, target))
  if (candidates.length === 1) return candidates[0]
  return null
}

function findBlockByIdOrLegacyNameWithinDistrict(
  blocks: PanchayatBlockRecord[],
  target: string,
  expectedDistrictId?: string,
): PanchayatBlockRecord | null {
  const strict = blocks.find((b) => matchesRecordNamesStrictId(b, target)) || null
  if (strict) {
    if (!expectedDistrictId || normalizeString(strict.districtId) === normalizeString(expectedDistrictId)) {
      return strict
    }
    return null
  }
  const candidates = blocks.filter((b) => {
    if (!matchesRecordNames(b, target)) return false
    if (expectedDistrictId && normalizeString(b.districtId) !== normalizeString(expectedDistrictId)) return false
    return true
  })
  if (candidates.length === 1) return candidates[0]
  return null
}

async function fetchInstitutionChildren(args: {
  type: InstitutionNodeType
  parentCode?: string | null
  districtCode?: string | null
}): Promise<InstitutionNode[]> {
  // 1. Try local/remote Node BFF first (progressive scope — only children of given parent).
  try {
    const json = await bffPost("/get-children", {
      type: args.type,
      parent_code: args.parentCode ?? null,
      district_code: args.districtCode ?? null,
    })
    // Backend returns: { success:true, count, data:[{code,type,parent_code,name:{en,hi,pa}}] }
    const rows: any[] = Array.isArray(json?.children)
      ? json.children
      : Array.isArray(json?.data)
        ? json.data
        : Array.isArray(json)
          ? json
          : []
    if (rows.length) return rows.map((r) => normalizeInstitutionNode(r))
  } catch (bffErr: any) {
    reportPerfDebug("C", "src/lib/panchayat-data.ts", "[BFF] /get-children failed, falling back", {
      error: String(bffErr?.message || bffErr),
      type: args.type,
    })
  }
  // 2. Fallback to existing Supabase Edge Function if configured.
  try {
    const payload = await invokeEdgeFunction<PanchayatChildrenPayload>(
      "panchayat-get-children",
      {
        type: args.type,
        parent_code: args.parentCode ?? null,
        district_code: args.districtCode ?? null,
      },
    )
    const rows: any[] = Array.isArray(payload?.children)
      ? payload.children
      : Array.isArray((payload as any)?.data)
        ? (payload as any).data
        : []
    return rows.map((r) => normalizeInstitutionNode(r))
  } catch (edgeErr: any) {
    reportPerfDebug("C", "src/lib/panchayat-data.ts", "[EDGE] panchayat-get-children fallback failed", {
      error: String(edgeErr?.message || edgeErr),
    })
    return []
  }
}

function normalizeInstitutionNode(r: any): InstitutionNode {
  const code = String(r?.code ?? r?.id ?? "").trim()
  return {
    code,
    id: code,
    type: (r?.type as InstitutionNodeType) ?? null,
    parent_code: r?.parent_code ?? null,
    district_id: r?.district_id ?? r?.district_code ?? null,
    name: r?.name ?? null,
    name_english: r?.name_english ?? r?.name?.en ?? null,
    name_hindi: r?.name_hindi ?? r?.name?.hi ?? null,
  }
}

async function fetchDistrictBootstrapData(): Promise<PanchayatDistrictRecord[]> {
  // 1. Try BFF districts (children of __root__/empty parent).
  try {
    const json = await bffPost("/get-children", { type: "district", parent_code: "", district_code: null })
    // Backend shape: { success:true, count, data:[{code,type,parent_code,name:{en,hi,pa}}] }
    const rows: any[] = Array.isArray(json?.children)
      ? json.children
      : Array.isArray(json?.data)
        ? json.data
        : Array.isArray(json)
          ? json
          : []
    if (rows.length) {
      return rows.map((r) => ({
        id: String(r?.id || r?.code || r?.name?.en || r?.name_english || "").trim(),
        names: normalizeNames(r?.names || { en: r?.name?.en || r?.name || r?.name_english || "", hi: r?.name?.hi || r?.name_hindi || "", pa: r?.name?.pa || "" }),
      })).filter((d) => d.id)
    }
  } catch (bffErr: any) {
    reportPerfDebug("C", "src/lib/panchayat-data.ts", "[BFF] district list failed, falling back to static", {
      error: String(bffErr?.message || bffErr),
    })
  }
  // 2. Static JSON fallback — do not hardcode districts. If BFF offline, fall back to bundled data.
  const res = await fetch("/data/v2/districts.json", { cache: "no-cache" })
  if (!res.ok) return []
  const payload = (await res.json()) as DistrictBootstrapPayload
  const districts = Array.isArray(payload?.districts) ? payload.districts : []
  return districts
    .map((entry) => ({
      id: String(entry?.id || "").trim(),
      names: normalizeNames(entry?.names || {}),
    }))
    .filter((entry) => entry.id)
}

async function fetchInstitutionData(
  code: string,
  districtCode: string,
  blockCode: string,
  type: "current" | "previous" = "current",
  lang: "en" | "hi" | "pa" = "en",
): Promise<PanchayatDataPayload | null> {
  const normalizedCode = String(code || "").trim()
  if (!normalizedCode) return null
  // 1. Node BFF first: canonical tables → V2GP-compatible shape (current|previous split by term flag)
  try {
    const json = await bffPost("/get-data", {
      code: normalizedCode,
      district_code: districtCode,
      block_code: blockCode,
      type,
      lang,
    })
    if (json && (json?.data || json?.panchayat)) {
      return json as PanchayatDataPayload
    }
  } catch (bffErr: any) {
    if (bffErr && typeof bffErr === "object" && (bffErr as any).status === 404) return null
    reportPerfDebug("C", "src/lib/panchayat-data.ts", "[BFF] /get-data failed, fallback", {
      code, type, error: String(bffErr?.message || bffErr),
    })
  }
  // 2. Fallback to Supabase Edge Function
  try {
    return await invokeEdgeFunction<PanchayatDataPayload>(
      "panchayat-get-data",
      {
        code: normalizedCode,
        district_code: districtCode,
        block_code: blockCode,
        type,
      },
    )
  } catch (error: any) {
    if (error && typeof error === "object" && (error as any).status === 404) return null
    reportPerfDebug("C", "src/lib/panchayat-data.ts", "[EDGE] panchayat-get-data failed, falling back to static shard", {
      code, type, error: String(error?.message || error),
    })
  }
  // 3. DATA17-REGRESSION-FIX-01: Scoped per-district static institution fallback
  // Resolution order: BFF /get-data → Edge panchayat-get-data → static v2|v2_prev
  // Anti-scraping/progressive: fetches ONLY the ONE district shard (~50KB) that
  // contains the selected GP. Never downloads full 6,222-GP dataset.
  // NPL/OPL split: type=current → /data/v2/districts/<slug>.json (NPL)
  //                type=previous → /data/v2_prev/districts/<slug>.json (OPL)
  // Identity: enterprise-strict hierarchical match — block then GP, with
  // strict-id primary, 3-language names secondary fallback (never display
  // name as sole identity).
  try {
    // (a) Resolve district → canonical file slug via _index.json (any language)
    const distRecord = districtCode ? await resolveDistrictRecord(districtCode).catch(() => null) : null
    const distEnName = String(
      distRecord?.names?.en
      ?? distRecord?.id
      ?? districtCode
      ?? ""
    ).trim()
    const normDistKey = normalizeString(distEnName)
    let slug: string | null = null
    if (normDistKey) {
      try {
        const idxRes = await fetch(`/data/v2/districts/_index.json`, { cache: "no-cache" })
        if (idxRes.ok) {
          const idx: Record<string, string> = await idxRes.json()
          slug = idx[normDistKey] || null
        }
      } catch {
        // index unavailable; fall through to direct slug heuristic below
      }
    }
    if (!slug) {
      // Heuristic: convert space → underscore for multi-word districts
      // (handles "Charkhi Dadri" → "charkhi_dadri" when index fails)
      slug = normDistKey ? normDistKey.replace(/\s+/g, "_") : ""
    }
    if (!slug) return null

    // (b) Select NPL vs OPL directory by record type
    const dataDir = type === "previous" ? "v2_prev" : "v2"
    const shardRes = await fetch(`/data/${dataDir}/districts/${slug}.json`, { cache: "no-cache" })
    if (!shardRes.ok) return null
    const shard: any = await shardRes.json()
    if (!shard || !Array.isArray(shard.blocks)) return null

    const districtNames: V2Names = {
      en: String(shard?.names?.en ?? distEnName).trim(),
      hi: String(shard?.names?.hi ?? distEnName).trim(),
      pa: String(shard?.names?.pa ?? distEnName).trim(),
    }
    const blockNamesEmpty: V2Names = { en: "", hi: "", pa: "" }

    // (c) Cross-reference enterprise identity — list-loader IDs (numeric codes,
    // technical IDs like D_*) vs institution-shard IDs (display names).
    // ResolveBlockContext + resolveGpRecord reuse the 3-source list chain so we
    // obtain BOTH the incoming technical key AND the authoritative 3-language
    // display names — we match against ALL of them (strict-id primary, names
    // secondary, but across the full identity set).  This satisfies Step 5:
    // technical codes are primary identity but we handle legacy shard naming.
    const resolvedBlock = blockCode
      ? await resolveBlockContext(blockCode, districtCode).catch(() => null)
      : null
    const resolvedGp = normalizedCode
      ? await resolveGpRecord(normalizedCode, districtCode, blockCode).catch(() => null)
      : null

    // Build a deduplicated set of normalized identity tokens we will accept for
    // block and GP matching.  Tokens are ordered by authority: strict technical
    // id first, then each language display name separately.
    const blockIdentityTokens: string[] = []
    const addBlockToken = (v: unknown) => {
      const s = normalizeString(v)
      if (s && !blockIdentityTokens.includes(s)) blockIdentityTokens.push(s)
    }
    addBlockToken(blockCode)
    if (resolvedBlock?.block) {
      addBlockToken(resolvedBlock.block.id)
      addBlockToken(resolvedBlock.block.names?.en)
      addBlockToken(resolvedBlock.block.names?.hi)
      addBlockToken(resolvedBlock.block.names?.pa)
    }
    const gpIdentityTokens: string[] = []
    const addGpToken = (v: unknown) => {
      const s = normalizeString(v)
      if (s && !gpIdentityTokens.includes(s)) gpIdentityTokens.push(s)
    }
    addGpToken(normalizedCode)
    if (resolvedGp) {
      addGpToken(resolvedGp.id)
      addGpToken(resolvedGp.names?.en)
      addGpToken(resolvedGp.names?.hi)
      addGpToken(resolvedGp.names?.pa)
    }

    // (d) Locate block within this district shard — strict id primary,
    // cross-referenced block-identity tokens secondary.
    let matchedBlock: any = null
    let matchedBlockNames: V2Names = { ...blockNamesEmpty }
    if (blockIdentityTokens.length) {
      const blockStrict = shard.blocks.find((b: any) => {
        const idNorm = normalizeString(b?.id)
        const codeNorm = normalizeString(b?.code)
        return blockIdentityTokens.some((t) => t === idNorm || t === codeNorm)
      })
      if (blockStrict) {
        matchedBlock = blockStrict
      } else {
        const candidates = shard.blocks.filter((b: any) => {
          const idNorm = normalizeString(b?.id)
          if (blockIdentityTokens.some((t) => t === idNorm)) return true
          const bNames: any = b?.names || {}
          return Object.values(bNames).some(
            (n: any) => blockIdentityTokens.includes(normalizeString(n)),
          )
        })
        if (candidates.length === 1) matchedBlock = candidates[0]
      }
    }
    if (matchedBlock) {
      matchedBlockNames = {
        en: String(matchedBlock?.names?.en ?? matchedBlock?.id ?? blockCode ?? "").trim(),
        hi: String(matchedBlock?.names?.hi ?? matchedBlock?.names?.en ?? "").trim(),
        pa: String(matchedBlock?.names?.pa ?? matchedBlock?.names?.en ?? "").trim(),
      }
    }

    // (e) Locate GP — candidates are either scoped to matched block (ideal)
    // or flat-mapped across all district blocks as defensive fallback.
    const scopedGps: any[] = matchedBlock && Array.isArray(matchedBlock.gps)
      ? matchedBlock.gps
      : shard.blocks.flatMap((b: any) => (Array.isArray(b?.gps) ? b.gps : []))

    let matchedGp: any = null
    if (gpIdentityTokens.length && scopedGps.length) {
      const gpStrict = scopedGps.find((g: any) => {
        const idNorm = normalizeString(g?.id)
        const codeNorm = normalizeString(g?.code)
        return gpIdentityTokens.some((t) => t === idNorm || t === codeNorm)
      })
      if (gpStrict) {
        matchedGp = gpStrict
      } else {
        const candidates = scopedGps.filter((g: any) => {
          const idNorm = normalizeString(g?.id)
          if (gpIdentityTokens.some((t) => t === idNorm)) return true
          const gNames: any = g?.names || {}
          return Object.values(gNames).some(
            (n: any) => gpIdentityTokens.includes(normalizeString(n)),
          )
        })
        if (candidates.length === 1) matchedGp = candidates[0]
      }
    }
    if (!matchedGp) return null

    // (f) Coerce static V2GP-shaped object into PanchayatDataPayload so the
    // existing downstream buildInstitutionRecord path handles it identically
    // to BFF/Edge returns. Same GP identity reused across NPL ↔ OPL.
    const gpNames: V2Names = {
      en: String(matchedGp?.names?.en ?? matchedGp?.id ?? normalizedCode ?? "").trim(),
      hi: String(matchedGp?.names?.hi ?? matchedGp?.names?.en ?? "").trim(),
      pa: String(matchedGp?.names?.pa ?? matchedGp?.names?.en ?? "").trim(),
    }
    const sarpanchRaw = matchedGp?.sarpanch && typeof matchedGp.sarpanch === "object"
      ? matchedGp.sarpanch
      : null
    const sarpanchNames: V2Names = {
      en: String(sarpanchRaw?.en ?? "").trim(),
      hi: String(sarpanchRaw?.hi ?? "").trim(),
      pa: String(sarpanchRaw?.pa ?? "").trim(),
    }
    const wardsRaw: any[] = Array.isArray(matchedGp?.wards) ? matchedGp.wards : []

    const buildLangRec = (lang: "en" | "hi" | "pa") => ({
      id: String(matchedGp?.id ?? normalizedCode),
      code: String(matchedGp?.id ?? normalizedCode),
      names: gpNames,
      name: gpNames[lang] || gpNames.en || "",
      district: districtNames[lang] || districtNames.en || "",
      districtNames,
      block: matchedBlockNames[lang] || matchedBlockNames.en || "",
      blockNames: matchedBlockNames,
      sarpanch: {
        names: sarpanchNames,
        name: sarpanchNames[lang] || sarpanchNames.en || "",
      },
      wards: wardsRaw
        .filter((w: any) => w && Number(w?.number) > 0)
        .map((w: any) => {
          const wNames: V2Names = {
            en: String(w?.names?.en ?? "").trim(),
            hi: String(w?.names?.hi ?? "").trim(),
            pa: String(w?.names?.pa ?? "").trim(),
          }
          return {
            ward_number: Number(w.number),
            number: Number(w.number),
            ward_name: wNames[lang] || wNames.en || "",
            member: { names: wNames, name: wNames[lang] || wNames.en || "" },
            name: wNames,
          }
        }),
    })

    const payload: PanchayatDataPayload = {
      code: normalizedCode,
      type: type === "previous" ? "previous" : "current",
      category: "gp",
      panchayat: {
        id: String(matchedGp?.id ?? normalizedCode),
        code: String(matchedGp?.id ?? normalizedCode),
        type: type === "previous" ? "previous" : "current",
        category: "gp",
        district: districtNames.en || "",
        block: matchedBlockNames.en || "",
        gram_panchayat: gpNames.en || "",
        data: {
          en: buildLangRec("en"),
          hi: buildLangRec("hi"),
          pa: buildLangRec("pa"),
        },
      },
    }
    return payload
  } catch (staticErr: any) {
    reportPerfDebug("C", "src/lib/panchayat-data.ts", "[STATIC] institution fallback failed", {
      code, type, districtCode, blockCode, error: String(staticErr?.message || staticErr),
    })
    return null
  }
}

async function loadDistrictListData(): Promise<PanchayatDistrictRecord[]> {
  await ensureDatasetVersion()
  const cached = readFreshCacheEntry(districtListCache)
  if (cached) return cached.value
  if (districtListInFlight) return districtListInFlight

  const request = (async () => {
    reportPerfDebug("C", "src/lib/panchayat-data.ts", "[DEBUG] District list fetch started", {
      endpoint: "/data/v2/districts.json",
      type: "district",
    })
    const records = await fetchDistrictBootstrapData()
    districtListCache = { value: records, expiresAt: Date.now() + DISTRICT_TTL_MS }
    reportPerfDebug("C", "src/lib/panchayat-data.ts", "[DEBUG] District list fetch completed", {
      endpoint: "/data/v2/districts.json",
      districtCount: records.length,
    })
    return records
  })()

  districtListInFlight = request
  try {
    return await request
  } finally {
    if (districtListInFlight === request) {
      districtListInFlight = null
    }
  }
}

async function resolveDistrictRecord(districtId: string): Promise<PanchayatDistrictRecord | null> {
  const districts = await loadDistrictListData()
  return findDistrictByIdOrLegacyName(districts, districtId)
}

async function loadBlockListData(districtId: string): Promise<PanchayatBlockRecord[]> {
  await ensureDatasetVersion()
  const district = await resolveDistrictRecord(districtId)
  if (!district) return []
  const cacheKey = normalizeDistrictKey(district.id)
  const cached = readFreshCacheEntry(blockListCache.get(cacheKey))
  if (cached) return cached.value
  blockListCache.delete(cacheKey)
  const inFlight = blockListInFlightCache.get(cacheKey)
  if (inFlight) return inFlight

  const request = (async () => {
    let children: any[] = []
    try {
      children = await fetchInstitutionChildren({ type: "block", parentCode: district.id })
    } catch {
      children = []
    }

    // DATA08D: static V2 JSON fallback for blocks (mirrors district bootstrap pattern).
    // Static shard filenames use stable-name convention:
    //   blocks/d_<normalized_en>.json
    // e.g. district en name "Sirsa" -> /data/v2/blocks/d_sirsa.json
    // This always matches the static generator naming regardless of whether district.id above is
    // a display name (from BFF) or a stable technical id (D_sirsa).
    if (!children || !children.length) {
      try {
        const enName = String(district.names?.en || district.id || '').trim()
        const shardId = `d_${normalizeString(enName)}`
        const res = await fetch(`/data/v2/blocks/${shardId}.json`, { cache: "no-cache" })
        if (res.ok) {
          const json: any = await res.json()
          if (json && Array.isArray(json.blocks)) {
            children = json.blocks
          }
        }
      } catch {
        // fallback unavailable; empty result safe.
      }
    }

    const blocks = children.map((entry) => ({
      id: String(entry?.id || entry?.code || "").trim(),
      names: normalizeBlockNodeNames(entry),
      districtId: String(entry?.districtId || entry?.district_id || district.id).trim() || district.id,
    })).filter((entry) => entry.id)
    blockListCache.set(cacheKey, { value: blocks, expiresAt: Date.now() + BLOCK_TTL_MS })
    for (const block of blocks) {
      loadedDistrictByBlockKey.set(normalizeBlockKey(block.id, district.id), district.id)
      loadedDistrictByBlockKey.set(normalizeBlockKey(block.names.en, district.id), district.id)
      loadedDistrictByBlockKey.set(normalizeBlockKey(block.names.hi, district.id), district.id)
      loadedDistrictByBlockKey.set(normalizeBlockKey(block.names.pa, district.id), district.id)
    }
    return blocks
  })()

  blockListInFlightCache.set(cacheKey, request)
  try {
    return await request
  } finally {
    if (blockListInFlightCache.get(cacheKey) === request) {
      blockListInFlightCache.delete(cacheKey)
    }
  }
}

async function resolveBlockContext(blockId: string, districtId?: string): Promise<{ district: PanchayatDistrictRecord; block: PanchayatBlockRecord } | null> {
  if (districtId) {
    const district = await resolveDistrictRecord(districtId)
    if (!district) return null
    const blocks = await loadBlockListData(district.id)
    const block = findBlockByIdOrLegacyNameWithinDistrict(blocks, blockId, district.id)
    if (!block) return null
    return { district, block }
  }

  const districts = await loadDistrictListData()
  for (const district of districts) {
    const blocksEntry = readFreshCacheEntry(blockListCache.get(normalizeDistrictKey(district.id)))
    if (!blocksEntry) continue
    const block = findBlockByIdOrLegacyNameWithinDistrict(blocksEntry.value, blockId, district.id)
    if (block) return { district, block }
  }
  return null
}

async function loadGpListData(blockId: string, districtId?: string): Promise<PanchayatGramPanchayatRecord[]> {
  await ensureDatasetVersion()
  const context = await resolveBlockContext(blockId, districtId)
  if (!context) return []
  const cacheKey = normalizeBlockKey(context.block.id, context.district.id)
  const cached = readFreshCacheEntry(gpListCache.get(cacheKey))
  if (cached) return cached.value
  gpListCache.delete(cacheKey)
  const inFlight = gpListInFlightCache.get(cacheKey)
  if (inFlight) return inFlight

  const request = (async () => {
    let children: any[] = []
    try {
      children = await fetchInstitutionChildren({ type: "gp", parentCode: context.block.id, districtCode: context.district.id })
    } catch {
      children = []
    }

    // DATA08D: static V2 JSON fallback for GPs (mirrors district bootstrap pattern).
    // Static shard filenames use stable-name convention:
    //   gps/d_<district_en>/b_d_<district_en>_<block_en>.json
    // e.g. district Sirsa + block Sirsa -> /data/v2/gps/d_sirsa/b_d_sirsa_sirsa.json
    // Always use display names (en) for shard key because that matches the static generator.
    if (!children || !children.length) {
      try {
        const distEn = String(context.district.names?.en || context.district.id || '').trim()
        const blockEn = String(context.block.names?.en || context.block.id || '').trim()
        const dKey = `d_${normalizeString(distEn)}`
        const bKey = `b_${dKey}_${normalizeString(blockEn)}`
        const res = await fetch(`/data/v2/gps/${dKey}/${bKey}.json`, { cache: "no-cache" })
        if (res.ok) {
          const json: any = await res.json()
          if (json && Array.isArray(json.gps)) {
            children = json.gps
          }
        }
      } catch {
        // fallback unavailable; empty result safe.
      }
    }

    const gps = children.map((entry) => ({
      id: String(entry?.id || entry?.code || "").trim(),
      names: normalizeGpNodeNames(entry),
      districtId: context.district.id,
      blockId: context.block.id,
    })).filter((entry) => entry.id)
    gpListCache.set(cacheKey, { value: gps, expiresAt: Date.now() + GP_TTL_MS })
    for (const gp of gps) {
      loadedDistrictByGpKey.set(normalizeGpKey(gp.id, context.district.id, context.block.id), context.district.id)
      loadedDistrictByGpKey.set(normalizeGpKey(gp.names.en, context.district.id, context.block.id), context.district.id)
      loadedDistrictByGpKey.set(normalizeGpKey(gp.names.hi, context.district.id, context.block.id), context.district.id)
      loadedDistrictByGpKey.set(normalizeGpKey(gp.names.pa, context.district.id, context.block.id), context.district.id)
      loadedBlockByGpKey.set(normalizeGpKey(gp.id, context.district.id, context.block.id), context.block.id)
      loadedBlockByGpKey.set(normalizeGpKey(gp.names.en, context.district.id, context.block.id), context.block.id)
      loadedBlockByGpKey.set(normalizeGpKey(gp.names.hi, context.district.id, context.block.id), context.block.id)
      loadedBlockByGpKey.set(normalizeGpKey(gp.names.pa, context.district.id, context.block.id), context.block.id)
    }
    return gps
  })()

  gpListInFlightCache.set(cacheKey, request)
  try {
    return await request
  } finally {
    if (gpListInFlightCache.get(cacheKey) === request) {
      gpListInFlightCache.delete(cacheKey)
    }
  }
}

async function resolveGpRecord(gpId: string, districtId?: string, blockId?: string): Promise<PanchayatGramPanchayatRecord | null> {
  if (blockId) {
    const gps = await loadGpListData(blockId, districtId).catch(() => [])
    const strict = gps.find((g) => matchesRecordNamesStrictId(g, gpId)) || null
    if (strict) return strict
    const candidates = gps.filter((g) => matchesRecordNames(g, gpId))
    return candidates.length === 1 ? candidates[0] : (candidates[0] || null)
  }

  for (const gps of gpListCache.values()) {
    if (Date.now() >= gps.expiresAt) continue
    const strict = gps.value.find((g) => matchesRecordNamesStrictId(g, gpId)) || null
    if (strict) return strict
  }
  for (const gps of gpListCache.values()) {
    if (Date.now() >= gps.expiresAt) continue
    const candidates = gps.value.filter((g) => matchesRecordNames(g, gpId))
    if (candidates.length === 1) return candidates[0]
  }

  return null
}

function personToV2Names(p: any): V2Names {
  if (!p || typeof p !== "object") return { ...EMPTY_NAMES }
  return {
    en: String(p?.names?.en ?? p?.name ?? p?.en ?? "").trim(),
    hi: String(p?.names?.hi ?? p?.hi ?? "").trim(),
    pa: String(p?.names?.pa ?? p?.pa ?? "").trim(),
  }
}

function buildInstitutionRecord(payload: PanchayatDataPayload, resolved: {
  gpCode: string
  districtId?: string
  blockId?: string
}): PanchayatInstitutionRecord | null {
  const panchayat = payload?.panchayat && typeof payload.panchayat === "object" ? payload.panchayat : null
  const allLangs = panchayat?.data && typeof panchayat.data === "object"
    ? panchayat.data
    : payload?.data && typeof payload.data === "object"
      ? payload.data
      : null
  if (!allLangs) return null
  // allLangs = { en, hi, pa } each containing: { id, code, names, name, district, districtNames, block, blockNames, sarpanch, chairperson, chairpersons, wards, members, designations, type }

  const first = allLangs.en || allLangs.hi || allLangs.pa || {}
  const names: V2Names = {
    en: String(first?.names?.en ?? first?.name ?? panchayat?.gram_panchayat ?? "").trim(),
    hi: String(first?.names?.hi ?? (allLangs.hi as any)?.name ?? "").trim(),
    pa: String(first?.names?.pa ?? (allLangs.pa as any)?.name ?? "").trim(),
  }
  const districtNames: V2Names = {
    en: String(first?.districtNames?.en ?? first?.district ?? panchayat?.district ?? "").trim(),
    hi: String(first?.districtNames?.hi ?? (allLangs.hi as any)?.district ?? "").trim(),
    pa: String(first?.districtNames?.pa ?? (allLangs.pa as any)?.district ?? "").trim(),
  }
  const blockNames: V2Names = {
    en: String(first?.blockNames?.en ?? first?.block ?? panchayat?.block ?? "").trim(),
    hi: String(first?.blockNames?.hi ?? (allLangs.hi as any)?.block ?? "").trim(),
    pa: String(first?.blockNames?.pa ?? (allLangs.pa as any)?.block ?? "").trim(),
  }
  const sarpanch: V2Names = personToV2Names(first?.sarpanch)

  // Collate wards from any language into V2Ward form using the ward member's name for display
  const wardByNumber = new Map<number, { en: string; hi: string; pa: string }>()
  for (const lang of ["en", "hi", "pa"] as const) {
    const rec = (allLangs as any)?.[lang]
    if (!rec || !Array.isArray(rec.wards)) continue
    for (const w of rec.wards) {
      const num = Number(w?.ward_number ?? w?.number)
      if (!Number.isFinite(num) || num <= 0) continue
      const memberName = String(
        w?.member?.names?.[lang]
        ?? w?.member?.name
        ?? w?.ward_name
        ?? w?.name?.[lang]
        ?? w?.name
        ?? ""
      ).trim()
      if (!wardByNumber.has(num)) wardByNumber.set(num, { en: "", hi: "", pa: "" })
      const cur = wardByNumber.get(num)!
      cur[lang] = cur[lang] || memberName
    }
  }
  const wards: V2Ward[] = Array.from(wardByNumber.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([number, n]) => ({ number, names: { en: n.en, hi: n.hi, pa: n.pa } }))

  return {
    id: String(panchayat?.id ?? first?.id ?? resolved.gpCode).trim() || resolved.gpCode,
    code: String(panchayat?.code ?? payload?.code ?? first?.code ?? resolved.gpCode).trim() || resolved.gpCode,
    type: panchayat?.type === "previous" ? "previous" : "current",
    category: "gp",
    names,
    districtId: String(resolved.districtId ?? "").trim() || districtNames.en,
    blockId: String(resolved.blockId ?? "").trim() || blockNames.en,
    districtNames,
    blockNames,
    sarpanch,
    wards,
    latitude: typeof (panchayat as any)?.latitude === "number" ? (panchayat as any).latitude : null,
    longitude: typeof (panchayat as any)?.longitude === "number" ? (panchayat as any).longitude : null,
    members: panchayat?.members ?? first?.members ?? null,
    rawData: allLangs as Record<string, any>,
  }
}

const defaultPanchayatDataProvider: PanchayatDataProvider = {
  async getDistricts() {
    return loadDistrictListData()
  },
  async getBlocks(districtId: string) {
    return loadBlockListData(districtId)
  },
  async getGramPanchayats(blockId: string, districtId?: string) {
    return loadGpListData(blockId, districtId)
  },
  async getInstitution(gpId: string, districtId?: string, blockId?: string, recordType?: "current" | "previous", lang?: "en" | "hi" | "pa") {
    await ensureDatasetVersion()
    const normalizedGpId = String(gpId || "").trim()
    if (!normalizedGpId) return null
    const resolvedRecordType = recordType === "previous" ? "previous" : "current"
    const effectiveLang: "en" | "hi" | "pa" = lang === "hi" || lang === "pa" ? lang : "en"

    const gpRecord = await resolveGpRecord(normalizedGpId, districtId, blockId)
    const effectiveGpId = gpRecord?.id || normalizedGpId
    const effectiveDistrictId = String(
      gpRecord?.districtId
      || districtId
      || loadedDistrictByGpKey.get(normalizeGpKey(effectiveGpId, districtId, blockId))
      || "",
    ).trim()
    const effectiveBlockId = String(
      gpRecord?.blockId
      || blockId
      || loadedBlockByGpKey.get(normalizeGpKey(effectiveGpId, districtId, blockId))
      || "",
    ).trim()

    const cacheKey = `${resolvedRecordType}::${normalizeGpKey(effectiveGpId, effectiveDistrictId, effectiveBlockId)}`
    const cached = readFreshCacheEntry(institutionCache.get(cacheKey))
    if (cached) return cached.value
    institutionCache.delete(cacheKey)
    const inFlight = institutionInFlightCache.get(cacheKey)
    if (inFlight) return inFlight

    const request = (async () => {
      if (!effectiveDistrictId || !effectiveBlockId) {
        institutionCache.set(cacheKey, { value: null, expiresAt: Date.now() + INSTITUTION_TTL_MS })
        return null
      }
      const payload = await fetchInstitutionData(effectiveGpId, effectiveDistrictId, effectiveBlockId, resolvedRecordType, effectiveLang)
      if (!payload) {
        institutionCache.set(cacheKey, { value: null, expiresAt: Date.now() + INSTITUTION_TTL_MS })
        return null
      }
      const institution = buildInstitutionRecord(payload, {
        gpCode: effectiveGpId,
        districtId: effectiveDistrictId,
        blockId: effectiveBlockId,
      })
      institutionCache.set(cacheKey, { value: institution, expiresAt: Date.now() + INSTITUTION_TTL_MS })
      return institution
    })()

    institutionInFlightCache.set(cacheKey, request)
    try {
      return await request
    } finally {
      if (institutionInFlightCache.get(cacheKey) === request) {
        institutionInFlightCache.delete(cacheKey)
      }
    }
  },
}

export function getPanchayatDataProvider(): PanchayatDataProvider {
  return activePanchayatDataProvider || defaultPanchayatDataProvider
}

export function setPanchayatDataProvider(provider: PanchayatDataProvider | null | undefined): void {
  activePanchayatDataProvider = provider ?? null
}

export function invalidatePanchayatDatasetCache(): void {
  districtListCache = null
  districtListInFlight = null
  blockListCache.clear()
  blockListInFlightCache.clear()
  gpListCache.clear()
  gpListInFlightCache.clear()
  institutionCache.clear()
  institutionInFlightCache.clear()
  hierarchyInFlight = null
  loadedDistrictByBlockKey.clear()
  loadedDistrictByGpKey.clear()
  loadedBlockByGpKey.clear()
}

export async function getPanchayatHierarchy(): Promise<V2Hierarchy> {
  if (hierarchyInFlight) return hierarchyInFlight

  const request = (async () => {
    const provider = getPanchayatDataProvider()
    const districts = await provider.getDistricts()
    const fullDistricts = await Promise.all(districts.map(async (district) => {
      const blocks = await provider.getBlocks(district.id)
      const fullBlocks = await Promise.all(blocks.map(async (block) => {
        const gps = await provider.getGramPanchayats(block.id, district.id)
        return {
          id: block.id,
          names: normalizeNames(block.names),
          gps: gps.map((gp) => ({
            id: gp.id,
            names: normalizeNames(gp.names),
          })),
        }
      }))
      return {
        id: district.id,
        names: normalizeNames(district.names),
        blocks: fullBlocks,
      }
    }))
    return { districts: fullDistricts }
  })()

  hierarchyInFlight = request
  try {
    return await request
  } finally {
    if (hierarchyInFlight === request) {
      hierarchyInFlight = null
    }
  }
}

export async function fetchPanchayatDataset(): Promise<any> {
  return getPanchayatHierarchy()
}

export async function refreshPanchayatDataset(): Promise<any> {
  invalidatePanchayatDatasetCache()
  return getPanchayatHierarchy()
}

export function datasetToMapLang(dataset: any, lang: "en" | "hi" | "pa" = "en"): PanchayatDataMap {
  const map: PanchayatDataMap = {}
  for (const d of dataset?.districts || []) {
    const dName = d?.names?.[lang]
    if (!dName) continue
    map[dName] = map[dName] || {}
    for (const b of d?.blocks || []) {
      const bName = b?.names?.[lang]
      if (!bName) continue
      const gps = (b?.gps || []).map((g: any) => g?.names?.[lang]).filter((n: any) => !!n)
      map[dName][bName] = gps
    }
  }
  return map
}

export async function getPanchayatMap(lang: "en" | "hi" | "pa" = "en"): Promise<PanchayatDataMap> {
  const dataset = await getPanchayatHierarchy()
  const map = datasetToMapLang(dataset, lang)
  reportPerfDebug("E", "src/lib/panchayat-data.ts", "[DEBUG] Panchayat map built", {
    lang,
    districtCount: Object.keys(map).length,
  })
  return map
}

export async function refreshPanchayatMap(lang: "en" | "hi" | "pa" = "en"): Promise<PanchayatDataMap> {
  invalidatePanchayatDatasetCache()
  return getPanchayatMap(lang)
}

export async function verifyDatasetConnectivity(): Promise<{ jsonOk: boolean; csvOk: boolean; jsonStatus?: number; csvStatus?: number }> {
  try {
    await getPanchayatDataProvider().getDistricts()
    return {
      jsonOk: true,
      csvOk: true,
      jsonStatus: 200,
      csvStatus: 200,
    }
  } catch {
    return {
      jsonOk: false,
      csvOk: false,
      jsonStatus: 500,
      csvStatus: 500,
    }
  }
}
